import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../lib/authStore';
import { ROLE_LABELS } from '@stromo/shared';
import { formatInitials } from '../lib/format';
import { Logo } from './Logo';

interface NavItemSpec {
  to: string;
  label: string;
  icon: JSX.Element;
  adminOnly?: boolean;
}

const ICON = {
  projects: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7h18M3 12h18M3 17h18" />
    </svg>
  ),
  gantt: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="10" height="3" rx="1" />
      <rect x="7" y="10" width="12" height="3" rx="1" />
      <rect x="5" y="16" width="8" height="3" rx="1" />
    </svg>
  ),
  todos: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 11 12 14 22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  photos: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="11" r="2" />
      <path d="m21 17-5-5L9 19" />
    </svg>
  ),
  settings: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  audit: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 8v4l3 2" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  ),
  about: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  logout: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
};

const MAIN_NAV: NavItemSpec[] = [
  { to: '/projects', label: 'Projekte', icon: ICON.projects },
  { to: '/gantt', label: 'Terminprogramm', icon: ICON.gantt },
  { to: '/todos', label: 'Todos', icon: ICON.todos },
  { to: '/photos', label: 'Fotodokumentation', icon: ICON.photos },
];

const SETTINGS_NAV: NavItemSpec[] = [
  { to: '/settings/templates', label: 'Vorlagen', icon: ICON.settings },
  { to: '/settings/contacts', label: 'Kontakte', icon: ICON.settings },
  { to: '/settings/users', label: 'Benutzer', icon: ICON.settings, adminOnly: true },
  { to: '/settings/audit', label: 'Audit-Log', icon: ICON.audit, adminOnly: true },
];

interface SidebarProps {
  className?: string;
}

export function Sidebar({ className = '' }: SidebarProps) {
  const { user, clear } = useAuthStore();
  const navigate = useNavigate();

  if (!user) return null;

  const isAdmin = user.role === 'admin';
  const settingsItems = SETTINGS_NAV.filter((item) => !item.adminOnly || isAdmin);

  const handleLogout = () => {
    clear();
    navigate('/login', { replace: true });
  };

  return (
    <aside className={`sidebar ${className}`.trim()}>
      <Logo />
      <nav className="sidebar-nav">
        <div className="nav-section">Übersicht</div>
        {MAIN_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
          >
            {item.icon}
            <span className="nav-item-label">{item.label}</span>
          </NavLink>
        ))}

        {(isAdmin || user.role === 'project_manager') && (
          <>
            <div className="nav-section">Stammdaten</div>
            {settingsItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              >
                {item.icon}
                <span className="nav-item-label">{item.label}</span>
              </NavLink>
            ))}
          </>
        )}

        <div className="nav-section">System</div>
        <NavLink
          to="/about"
          className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
        >
          {ICON.about}
          <span className="nav-item-label">Über Stromo</span>
        </NavLink>
      </nav>

      <div className="nav-user">
        <div className="avatar" title={`${user.first_name} ${user.last_name}`}>
          {formatInitials(user.first_name, user.last_name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="user-name">
            {user.first_name} {user.last_name}
          </div>
          <div className="user-role">{ROLE_LABELS[user.role]}</div>
        </div>
        <button
          type="button"
          className="nav-item"
          style={{ width: 'auto', padding: '6px 8px', borderLeft: 'none' }}
          onClick={handleLogout}
          title="Abmelden"
          aria-label="Abmelden"
        >
          {ICON.logout}
        </button>
      </div>
    </aside>
  );
}
